import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import useMyAgencyData from '../../../../hooks/useMyAgencyData';
import createAxiosInstance from "../../../../utils/axiosInstance";
import { API_ENDPOINTS } from '../../../../utils/api';
import { userRoleMap } from '../../../../utils/helper';
import Loader from '../../../../Components/Loader';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  createdAt: string;
  userId?: string;
  active?: boolean;
}

const MyAccountPage = () => {
  const axiosInstance = createAxiosInstance();
  const myShelter = useMyAgencyData();
  const timeZoneInfo = (new Date().getTimezoneOffset() / 60) * -1;

  const [usersList, setUsersList] = useState<User[]>([]);
  const [isLoding, setIsLoding] = useState(true);

  useEffect(() => {
    if (myShelter) {
      getUsersList();
    }
  }, [myShelter]);

  const getUsersList = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.BEDFINDER.GETUSERS);

      const data = response.data; // Extract the actual data

      setUsersList(data);
      setIsLoding(false);
    } catch (error) {
      setIsLoding(false);
      console.log(error);
    }
  };

  const usersTableData = useMemo(
    () =>
      usersList
        .sort((a: any, b: any) =>
          ((a.firstName || '') + (a.lastName || ''))
            .toLowerCase()
            .localeCompare(((b.firstName || '') + (b.lastName || '')).toLowerCase())
        )
        .map((element: any) => ({
          name: element.firstName + " " + element.lastName,
          email: element.email,
          status: !element.userId
            ? 'Not registered'
            : element.active
              ? 'Active'
              : 'Inactive',
          role:
            Array.isArray(element.roles) && element.roles.length > 0
              ? userRoleMap[element.roles[0] as keyof typeof userRoleMap] ?? "Unknown"
              : "Unknown",
          referenceUrl: `/my-account/users/${element?.id}`,
          id: element?.id
        })),
    [usersList],
  );

  const firstAdmin = () => {
    if (usersList) {
      const first = usersList
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .find(({ roles }: { roles: string[] }) => ["admin", "shelter-admin"].includes(roles[0]));
      return first ?? null;
    }
    return null;
  }

  return (
    <>
      {isLoding && <Loader />}
      <div className='bg-white'>
        <div className="container py-4 md:py-10 2xl:py-[70px] xl:w-2/3">
          <div className="flex justify-between flex-wrap items-center">
            <h2 className='text-base lg:text-lg 2xl:text-2xl font-semibold'>My Shelter</h2>
            <a href={`${process.env.REACT_APP_KEYCLOAK_URL}/realms/ssc-portal/account/password?referrer=ssc-portal`} target="_blank" className='text-primary font-semibold'>Change Password</a>
          </div>
          <div className="border border-borderGray rounded-xl overflow-hidden mt-5">
            <table className="w-full text-xs lg:text-sm text-left rtl:md:text-right text-gray-500">
              <tbody>
                <tr className="bg-white md:border md:border-b-borderGray">
                  <th scope="row" className="px-4 2xl:px-6 py-3 font-medium text-gray-900 border md:border-r-borderGray md:border-b-borderGray md:text-right">
                    Shelter Name
                  </th>
                  <td className="px-4 2xl:px-6 py-3 text-left lg:w-4/6 text-gray-700">
                    {myShelter?.name}
                  </td>
                </tr>
                <tr className="bg-white md:border md:border-b-borderGray">
                  <th scope="row" className="px-4 2xl:px-6 py-3 font-medium text-gray-900 border md:border-r-borderGray md:border-b-borderGray md:text-right">
                    Administrator Name
                  </th>
                  <td className="px-4 2xl:px-6 py-3 text-left lg:w-4/6 text-gray-700">
                    {myShelter?.details?.admin?.name ?? (firstAdmin()?.firstName ? firstAdmin()?.firstName + " " + firstAdmin()?.lastName : "-")}
                  </td>
                </tr>
                <tr className="bg-white md:border md:border-b-borderGray">
                  <th scope="row" className="px-4 2xl:px-6 py-3 font-medium text-gray-900 border md:border-r-borderGray md:border-b-borderGray md:text-right">
                    Administrator Email
                  </th>
                  <td className="px-4 2xl:px-6 py-3 text-left lg:w-4/6 text-gray-700">
                    {(myShelter?.details?.admin?.email ?? firstAdmin()?.email) ? <Link className="text-primary" to={`mailto:${myShelter?.details?.admin?.email ?? firstAdmin()?.email}`}>{myShelter?.details?.admin?.email ?? firstAdmin()?.email}</Link> : "-"}
                  </td>
                </tr>
                <tr className="bg-white md:border md:border-b-borderGray">
                  <th scope="row" className="px-4 2xl:px-6 py-3 font-medium text-gray-900 border md:border-r-borderGray md:border-b-borderGray md:text-right">
                    Shelter Email
                  </th>
                  <td className="px-4 2xl:px-6 py-3 text-left lg:w-4/6 text-gray-700">
                    {myShelter?.details?.email ? <Link className="text-primary" to={`mailto:${myShelter?.details?.email}`}>{myShelter?.details?.email}</Link> : "-"}
                  </td>
                </tr>
                <tr className="bg-white md:border md:border-b-borderGray">
                  <th scope="row" className="px-4 2xl:px-6 py-3 font-medium text-gray-900 border md:border-r-borderGray md:border-b-borderGray md:text-right">
                    State
                  </th>
                  <td className="px-4 2xl:px-6 py-3 text-left lg:w-4/6 text-gray-700">
                    {myShelter?.details?.state ?? "-"}
                  </td>
                </tr>
                <tr className="bg-white md:border md:border-b-borderGray">
                  <th scope="row" className="px-4 2xl:px-6 py-3 font-medium text-gray-900 border md:border-r-borderGray md:border-b-borderGray md:text-right">
                    City
                  </th>
                  <td className="px-4 2xl:px-6 py-3 text-left lg:w-4/6 text-gray-700">
                    {myShelter?.details?.city ?? "-"}
                  </td>
                </tr>
                {(myShelter?.survivorPopulation?.length ?? 0) > 0 && (
                  <tr className="bg-white md:border md:border-b-borderGray">
                    <th scope="row" className="px-4 2xl:px-6 py-3 font-medium text-gray-900 border md:border-r-borderGray md:border-b-borderGray md:text-right">
                      Populations supported
                    </th>
                    <td className="px-4 2xl:px-6 py-3 text-left lg:w-4/6 text-gray-700">
                      {myShelter?.survivorPopulation?.join(", ")}
                      <div>
                        To update, please contact&nbsp;
                        <a
                          href="mailto:support@safesheltercollaborative.org"
                        >
                          <Link className="text-primary" to={`mailto:support@safesheltercollaborative.org`}>support@safesheltercollaborative.org</Link>
                        </a>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-10">
            <h2 className='text-base lg:text-lg 2xl:text-2xl font-semibold'>Time Zone Information</h2>
          </div>
          <div className="border border-borderGray rounded-xl overflow-hidden mt-5">
            <table className="w-full text-xs lg:text-sm text-left rtl:md:text-right text-gray-500">
              <tbody>
                <tr className="bg-white">
                  <th scope="row" className="px-4 2xl:px-6 py-3 font-medium text-gray-900 border md:border-r-borderGray md:md:text-right">
                    Current Time Zone
                  </th>
                  <td className="px-4 2xl:px-6 py-3 text-left lg:w-4/6 text-gray-700">
                    GMT{timeZoneInfo > 0 ? " +" : " -"}{timeZoneInfo}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex justify-between flex-wrap items-center mt-10">
            <h2 className='text-base lg:text-lg 2xl:text-2xl font-semibold'>Users</h2>
            <Link to={'/my-account/new-user'} className='btn btn-primary'>Create New User</Link>
          </div>


          <div className="relative overflow-x-auto sm:rounded-lg mt-5 md:border border-borderGray">
            <table className="table_responsive_block w-full text-xs 2xl:text-sm text-left rtl:md:text-right text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 2xl:px-6 py-3">
                    Name
                  </th>
                  <th scope="col" className="px-4 2xl:px-6 py-3">
                    Email
                  </th>
                  <th scope="col" className="px-4 2xl:px-6 py-3">
                    User Status
                  </th>
                  <th scope="col" className="px-4 2xl:px-6 py-3">
                    Role
                  </th>
                </tr>
              </thead>
              <tbody>
                {usersTableData.map((item: any, index: any) => (
                  <tr className="odd:bg-white even:bg-gray-50" key={index}>
                    <td data-title='Name' scope="row" className="px-4 2xl:px-6 py-3 font-medium text-secondary whitespace-nowrap ">
                      <Link className='text-primary' to={`/my-account/user-details/${item.id}`}>{item.name}</Link>
                    </td>
                    <td data-title='Email' className="px-4 2xl:px-6 py-3">
                      <a className='text-primary' href={`mailto:${item.email}`}><span className='truncate'>{item.email}</span></a>
                    </td>
                    <td data-title='User Status' className="px-4 2xl:px-6 py-3">
                      {item.status === "Active" ? <span className='badge badge-success'>{item.status}</span> : <span className='badge badge-danger'>{item.status}</span>}
                    </td>
                    <td data-title='Role' className="px-4 2xl:px-6 py-3">
                      {item.role}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className='flex justify-end mt-5'>
            <Link to="/bedfinder">
              <button
                className="btn btn-outline-gray py-2 2xl:py-3"
              >
                Go Back
              </button>
            </Link>
          </div>

        </div>
      </div>
    </>
  )
}

export default MyAccountPage